import { Bars3BottomRightIcon } from '@heroicons/react/24/outline'
import { memo, useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import { useDispatch, useSelector } from 'react-redux'
import { useDetectClickOutside } from 'hooks/useDetectClickOutside'
import { setIsOpened } from 'store/widget'
import { messagesActions, messagesSelectors } from 'store/messages'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { settingsActions, settingsSelectors } from 'store/settings'
import { Icon } from 'components/ui/Icon'
import _ from 'lodash'
import { showExportChatModal, showLoginModal } from '../managers/modal'
import { useGetBotMode } from 'hooks/useGetBotMode'
import { useGetSetting } from 'api/settings'
import { AppVersionBanner } from 'components/app-version/AppVersionBanner'

const WidgetHeaderComponent = () => {
  const dispatch = useDispatch()
  const {
    colors: { primary },
    setting,
    title,
    description,
  } = useGetSetting()
  const { t } = useTranslation()
  const chatId = useSelector(messagesSelectors.chatId)
  const isLogged = useSelector(settingsSelectors.isLogged)
  const { isAppMode, isWebMode } = useGetBotMode()
  const [showDropdown, setShowDropdown] = useState(false)
  const dropdownRef = useDetectClickOutside({
    setShowModal: setShowDropdown,
  })

  console.debug(primary)

  const dropdownMenu = useMemo(() => {
    return _.compact([
      isLogged && {
        title: t('logout'),
        callback: () => {
          dispatch(messagesActions.setChatId(undefined))
          dispatch(messagesActions.setChats({}))
          dispatch(settingsActions.setTicketReminderVisible(false))
          dispatch(settingsActions.logout())
        },
        icon: <Icon icon={['far', 'sign-out-alt']} className="tw-h-4 tw-w-4" />,
      },
      !isLogged && {
        title: t('login'),
        callback: showLoginModal,
        icon: <Icon icon={['far', 'sign-in-alt']} className="tw-h-4 tw-w-4" />,
      },
      isWebMode && {
        title: t('close'),
        icon: <Icon icon={['far', 'times']} className="tw-h-4 tw-w-4" />,
        callback: () => {
          dispatch(setIsOpened(false))
          setShowDropdown(false)
        },
      },
      !!chatId && {
        title: t('goBack'),
        icon: <Icon icon={['far', 'arrow-left']} className="tw-h-4 tw-w-4" />,
        callback: () => {
          dispatch(messagesActions.setChatId(undefined))
          dispatch(messagesActions.setBotTyping(false))
          dispatch(messagesActions.setUserTyping(true))
          dispatch(messagesActions.setUserTypingPlaceholder('typeMessage'))
          dispatch(settingsActions.setTicketReminderVisible(false))
          setShowDropdown(false)
        },
      },
      isLogged &&
        !!chatId && {
          title: t('exportChat'),
          callback: () => {
            setShowDropdown(false)
            showExportChatModal()
          },
          icon: <Icon icon={['far', 'file-export']} className="tw-h-4 tw-w-4" />,
        },
    ])
  }, [t, chatId, isLogged, isWebMode])

  return (
    <>
      <div
        className={clsx(
          'tw-relative tw-flex tw-cursor-default tw-items-center tw-space-x-4 tw-p-2 tw-shadow-lg tw-drop-shadow',
          `tw-h-[7rem] tw-min-h-[7rem] tw-max-h-[7rem]`,
          isAppMode ? '' : 'tw-rounded-t-5xl',
          'tw-bg-primary-600',
        )}
        style={{ color: 'white' }}
      >
        {setting?.logo?.full && (
          <div className="tw-shrink-0 tw-rounded-full tw-p-2">
            <img
              className="tw-h-12 tw-w-12 tw-rounded-md"
              src={setting?.logo?.thumbnail?.medium ?? setting?.logo}
              alt="Bot Logo"
            />
          </div>
        )}
        <div className="tw-w-full">
          <div className="tw-text-xl tw-font-semibold tw-antialiased">{title}</div>
          <p className="">{description}</p>
          {/* <AppVersionBanner /> */}
        </div>
        <motion.div whileHover={{ scale: 1.2 }} className="tw-flex" onClick={() => setShowDropdown(!showDropdown)}>
          <Bars3BottomRightIcon className="tw-h-7 tw-w-7 tw-cursor-pointer" />
        </motion.div>
      </div>
      <div
        className={'tw-w-full tw-h-[2px]'}
        style={{
          background: `linear-gradient(90deg, rgba(14,187,179,0.2) 0%, rgba(14,187,179,1) 50%, rgba(14,187,179,0.2) 100%)`,
        }}
      />
      {showDropdown && (
        <div
          id="dropdown"
          className="tw-absolute tw-right-5 tw-top-16 tw-z-50 tw-w-fit tw-cursor-default  tw-divide-y tw-divide-gray-100 tw-rounded-xl tw-bg-primary-500 tw-shadow-lg"
          ref={dropdownRef}
        >
          <ul
            className="tw-rounded-lg tw-py-1 tw-text-sm"
            aria-labelledby="dropdownDefault"
            style={{
              backgroundColor: primary,
              color: 'white',
              border: `1px solid white`,
            }}
          >
            {dropdownMenu.map((item, idx) => {
              return (
                <div key={idx} className="tw-hover:opacity-70 tw-flex tw-cursor-pointer" onClick={item.callback}>
                  <div className="tw-flex tw-items-center tw-justify-center tw-pl-2">{item.icon}</div>
                  <div>
                    <span className="tw-block tw-px-2 tw-py-2">{item.title}</span>
                  </div>
                </div>
              )
            })}
          </ul>
        </div>
      )}
    </>
  )
}

export const WidgetHeader = memo(WidgetHeaderComponent)
